import type { ApiMaterialV1MaterialProductsListParams, MaterialProductInfoListItem } from 'api';
import { useApiMaterialV1MaterialProductsList } from 'api';
import { Form, useForm } from 'form';
import { useTranslation } from 'i18n';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, DataGrid, Page, Section } from 'ui';
import { ClassOutlined } from 'ui/icons';

import { MaterialProductsListFilters } from '../../components/MaterialProductsListFilters/MaterialProductsListFilter';
import { columns } from './columns';

export const MaterialProductsList = () => {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const form = useForm();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [filters, setFilters] = useState<Partial<ApiMaterialV1MaterialProductsListParams>>({});

  useEffect(() => {
    const subscription = form.watch((values) => {
      const nextFilters = Object.entries(values).reduce<Partial<ApiMaterialV1MaterialProductsListParams>>(
        (accumulator, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            accumulator[key as keyof ApiMaterialV1MaterialProductsListParams] = value;
          }
          return accumulator;
        },
        {}
      );

      setFilters((previousFilters) => {
        const previousEntries = Object.entries(previousFilters);
        const nextEntries = Object.entries(nextFilters);

        const hasSameEntries =
          previousEntries.length === nextEntries.length &&
          nextEntries.every(
            ([key, value]) => previousFilters[key as keyof ApiMaterialV1MaterialProductsListParams] === value
          );

        if (hasSameEntries) {
          return previousFilters;
        }

        setPaginationModel((current) => ({ ...current, page: 0 }));
        return nextFilters;
      });
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const queryParams = useMemo<ApiMaterialV1MaterialProductsListParams>(() => {
    return {
      page: paginationModel.page + 1,
      page_size: paginationModel.pageSize,
      ...filters,
    };
  }, [filters, paginationModel]);

  const { data, isFetching } = useApiMaterialV1MaterialProductsList(queryParams, {
    query: {
      placeholderData: (previousData) => previousData,
    },
  });

  const rows = data?.results ?? [];
  const rowCount = data?.count ?? 0;

  return (
    <Page>
      <Section
        title={t('materialProducts.list.title')}
        icon={ClassOutlined}
        action={
          <Button size="medium" component={Link} to="/ground-control/material-products/create">
            {t('materialProducts.list.actions.createMaterialProduct')}
          </Button>
        }
      >
        <Form {...form}>
          <MaterialProductsListFilters />
        </Form>
        <DataGrid<MaterialProductInfoListItem>
          autoHeight
          columns={columns}
          data-testid="material-products-list"
          density="compact"
          disableColumnFilter={true}
          filterDebounceMs={500}
          loading={isFetching}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          paginationModel={paginationModel}
          rowCount={rowCount}
          rows={rows}
          showColumnVerticalBorder
          showCellVerticalBorder
          sx={{ cursor: 'pointer' }}
          onRowClick={(params) => {
            navigate(`/ground-control/material-products/${params.row.id}`);
          }}
        />
      </Section>
    </Page>
  );
};
